/**
 * Generates consistent HTML content for print/PDF export with built-in logo handling.
 *
 * @param {Object} options
 * @param {string} options.reportTitle - Title of the report.
 * @param {string} [options.dateRangeText] - Date range string to display.
 * @param {string} options.generatedDate - Date string to display.
 * @param {string} options.tableHeaders - HTML string of <th> elements.
 * @param {string} options.tableRows - HTML string of <tr> elements.
 * @param {string} [options.footerText] - Optional footer text.
 * @param {string} [options.fallbackLogoText] - Text to show if logos fail to load.
 * @param {boolean} [options.showUniversityLogo=true] - Whether to show university logo.
 * @param {boolean} [options.showLrcLogo=true] - Whether to show LRC logo.
 * @param {string} [options.reportType='default'] - Type of report (determines column styling)
 * @returns {string} - Full HTML document as a string.
 */
export const generateExportHTML = ({
  reportTitle,
  generatedDate,
  tableHeaders,
  tableRows,
  footerText = "Library Management System Export",
  fallbackLogoText = "Library Resource Center",
  showUniversityLogo = true,
  showLrcLogo = true,
  reportType = "default", // 'finemanagement', 'mostloaned', or 'default'
}) => `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${reportTitle}</title>
  <style>
    @media print {
      body { margin: 0; }
      .no-print { display: none; }
    }
    
    body {
      font-family: Arial, sans-serif;
      margin: 20px;
      color: #333;
      line-height: 1.4;
    }
    
    .header {
      text-align: center;
      margin-bottom: 30px;
      border-bottom: 2px solid #333;
      padding-bottom: 15px;
      page-break-inside: avoid;
    }
    
    .logo-container {
      margin-bottom: 15px;
      height: 100px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 30px;
      flex-wrap: wrap;
    }
    
    .logo { 
      max-width: 150px; 
      max-height: 80px; 
      display: block;
      object-fit: contain;
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    .logo.loaded {
      opacity: 1;
    }
    
    .university-logo {
      max-width: 120px;
      max-height: 80px;
    }
    
    .lrc-logo {
      max-width: 180px;
      max-height: 80px;
    }
    
    .logo-fallback { 
      font-size: 18px; 
      color: #333; 
      font-weight: bold;
      background: #f5f5f5;
      padding: 20px;
      border: 2px solid #ddd;
      border-radius: 8px;
      display: none;
    }
    
    .header h1 {
      margin: 0 0 10px 0;
      color: rgb(0, 0, 0);
      font-size: 24px;
    }
    
    .header p {
      margin: 5px 0;
      color: #666;
      font-size: 14px;
    }
    
    .loading-indicator {
      color: #666;
      font-size: 14px;
      font-style: italic;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 20px;
      font-size: 12px;
      page-break-inside: auto;
      table-layout: fixed; /* Fixed layout for consistent column widths */
    }
    
    th {
      background-color: #225745;
      color: white;
      padding: 8px 6px;
      text-align: left;
      border: 1px solid #ddd;
      font-weight: bold;
      font-size: 14px;
      page-break-inside: avoid;
      page-break-after: avoid;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    td {
      padding: 6px;
      border: 1px solid #ddd;
      vertical-align: top;
      font-size: 12px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    ${
      reportType === "thesis"
        ? `
      /* Thesis/Special Collections specific column styles */
      th:nth-child(1), td:nth-child(1) { /* Title */
        width: 32.5%;
        white-space: normal;
        word-wrap: break-word;
        overflow-wrap: break-word;
        max-width: 0;
      }
      
      th:nth-child(2), td:nth-child(2) { /* Author(s) */
        width: 22.5%;
        white-space: normal;
        word-wrap: break-word;
        overflow-wrap: break-word;
        max-width: 0;
      }
      
      th:nth-child(3), td:nth-child(3) { /* Publication Year */
        width: 10%;
        text-align: center;
        white-space: nowrap;
      }
      
      th:nth-child(4), td:nth-child(4) { /* Collection Type */
        width: 15%;
        text-align: center;
        white-space: normal;
        word-wrap: break-word;
      }
      
      th:nth-child(5), td:nth-child(5) { /* Department */
        width: 10%;
        text-align: center;
        white-space: normal;
        word-wrap: break-word;
      }
      
      th:nth-child(6), td:nth-child(6) { /* Program */
        width: 10%;
        test-align: center;
        white-space: normal;
        word-wrap: break-word;
      }
      `
        : reportType === "finemanagement"
        ? `
    /* Fine Management specific column styles */
    th:nth-child(1), td:nth-child(1) { /* Patron Name */
      width: 25%;
      white-space: normal;
      word-wrap: break-word;
    }
    
    th:nth-child(2), td:nth-child(2) { /* Email */
      width: 30%;
      white-space: normal;
      word-wrap: break-word;
    }
    
    th:nth-child(3), td:nth-child(3) { /* Total Fine */
      width: 15%;
      text-align: right;
    }
    
    th:nth-child(4), td:nth-child(4) { /* Overdue Count */
      width: 15%;
      text-align: center;
    }
    
    th:nth-child(5), td:nth-child(5) { /* Loans Count */
      width: 15%;
      text-align: center;
    }
    `
        : reportType === "finehistory"
        ? `
    /* Fine History specific column styles */
    th:nth-child(1), td:nth-child(1) { /* Patron Name */
      width: 12%;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    
    th:nth-child(2), td:nth-child(2) { /* Email */
      width: 18%;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    
    th:nth-child(3), td:nth-child(3) { /* Document Title */
      width: 25%;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: 0; /* Force table layout to respect width */
    }
    
    th:nth-child(4), td:nth-child(4) { /* Item Barcode */
      width: 10%;
      text-align: center;
      font-family: monospace;
      font-size: 11px;
      white-space: normal;
      word-wrap: break-word;
    }
    
    th:nth-child(5), td:nth-child(5) { /* Fine Amount */
      width: 8%;
      text-align: right;
      white-space: nowrap;
      text-align: center;
    }
    
    th:nth-child(6), td:nth-child(6) { /* Status */
      width: 8%;
      text-align: center;
      white-space: nowrap;
    }
    
    th:nth-child(7), td:nth-child(7) { /* Payment Reference */
      width: 14%;
      font-family: monospace;
      font-size: 10px;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: 0; /* Force table layout to respect width */
    }
    
    /* Print-specific adjustments for Fine History */
    @media print {
      .finehistory-table th:nth-child(3), 
      .finehistory-table td:nth-child(3) { /* Document Title */
        font-size: 9px;
      }
      
      .finehistory-table th:nth-child(7), 
      .finehistory-table td:nth-child(7) { /* Payment Reference */
        font-size: 13px;
      }
    }
    `
        : reportType === "mostloaned"
        ? `
    /* Most Loaned Documents specific column styles */
    th:nth-child(1), td:nth-child(1) { /* Rank */
      width: 30px;
      text-align: center;
      max-width: 30px;
    }
    
    th:nth-child(2), td:nth-child(2) { /* Title */
      width: 150px;
      max-width: 180px;
      white-space: normal;
      word-wrap: break-word;
    }
    
    th:nth-child(3), td:nth-child(3) { /* Authors */
      width: 100px;
      max-width: 120px;
      white-space: normal;
      word-wrap: break-word;
    }
    
    th:nth-child(4), td:nth-child(4) { /* Edition */
      width: 25px;
      max-width: 25px;
      text-align: center;
    }
    
    th:nth-child(5), td:nth-child(5) { /* Year */
      width: 40px;
      max-width: 40px;
      text-align: center;
    }
    
    th:nth-child(6), td:nth-child(6) { /* Publisher */
      width: 80px;
      max-width: 100px;
      white-space: normal;
      word-wrap: break-word;
    }
    
    th:nth-child(7), td:nth-child(7) { /* Loans */
      width: 50px;
      max-width: 50px;
      text-align: center;
      font-weight: bold;
    }
    
    th:nth-child(8), td:nth-child(8) { /* Patrons */
      width: 50px;
      max-width: 50px;
      text-align: center;
    }
    
    th:nth-child(9), td:nth-child(9) { /* Top Patron */
      width: 80px;
      max-width: 100px;
      white-space: normal;
      word-wrap: break-word;
    }
    
    th:nth-child(10), td:nth-child(10) { /* Role */
      width: 60px;
      max-width: 80px;
    }
    
    th:nth-child(11), td:nth-child(11) { /* Top Loans */
      width: 40px;
      max-width: 40px;
      text-align: center;
    }
    
    th:nth-child(12), td:nth-child(12) { /* PID */
      width: 70px;
      max-width: 70px;
      font-family: monospace;
      font-size: 10px;
    }
    `
        : `
    /* Default column styles - flexible layout */
    td {
      white-space: normal;
      word-wrap: break-word;
    }
    `
    }
    
    tr:nth-child(even) {
      background-color: #f9f9f9;
    }
    
    tr:hover {
      background-color: #f5f5f5;
    }
    
    .footer {
      margin-top: 30px;
      text-align: center;
      font-size: 12px;
      color: #666;
      border-top: 1px solid #ddd;
      padding-top: 15px;
      page-break-inside: avoid;
    }
    
    .print-button {
      background-color: #3498db;
      color: white;
      padding: 10px 20px;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-size: 14px;
      margin-bottom: 20px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    
    .print-button:hover {
      background-color: #2980b9;
    }
    
    /* Print-specific styles */
    @media print {
      .print-button { 
        display: none !important; 
      }
      
      .loading-indicator {
        display: none !important;
      }
      
      body {
        padding: 15px;
        font-size: 11px;
      }
      
      .header {
        margin-bottom: 20px;
      }
      
      table {
        page-break-inside: auto;
        font-size: 10px;
      }
      
      th {
        font-size: 10px;
        padding: 4px;
      }
      
      td {
        font-size: 10px;
        padding: 4px;
      }
      
      tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
      
      thead {
        display: table-header-group;
      }
      
      tfoot {
        display: table-footer-group;
      }
    }
    
    /* Responsive adjustments */
    @media screen and (max-width: 768px) {
      body {
        padding: 10px;
      }
      
      table {
        font-size: 9px;
      }
      
      th, td {
        padding: 3px;
        font-size: 9px;
      }
      
      .logo-container {
        gap: 15px;
      }
      
      .header h1 {
        font-size: 20px;
      }
    }
  </style>
  <script>
    // Built-in logo management system
    class LogoManager {
      constructor() {
        this.loadedLogos = 0;
        this.totalLogos = 0;
        this.logoPromises = [];
        this.init();
      }
      
      init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.loadLogos());
        } else {
          this.loadLogos();
        }
      }
      
      async loadLogos() {
        const logoContainer = document.querySelector('.logo-container');
        const fallback = document.querySelector('.logo-fallback');
        const loadingIndicator = document.querySelector('.loading-indicator');
        
        if (!logoContainer) return;
        
        // Get base URL for assets
        const baseUrl = window.location.origin + (window.PUBLIC_URL || '');
        
        // Define logo configurations
        const logoConfigs = [];
        
        ${
          showUniversityLogo
            ? `
        logoConfigs.push({
          src: baseUrl + '/images/university-logo.png',
          alt: 'University Logo',
          className: 'logo university-logo',
          fallbackSrcs: [
            baseUrl + '/images/university-logo.jpg',
            baseUrl + '/images/uni-logo.png',
            baseUrl + '/assets/university-logo.png'
          ]
        });
        `
            : ""
        }
        
        ${
          showLrcLogo
            ? `
        logoConfigs.push({
          src: baseUrl + '/images/lrc-logo.png',
          alt: 'LRC Logo', 
          className: 'logo lrc-logo',
          fallbackSrcs: [
            baseUrl + '/images/lrc-logo.jpg',
            baseUrl + '/images/library-logo.png',
            baseUrl + '/assets/lrc-logo.png'
          ]
        });
        `
            : ""
        }
        
        this.totalLogos = logoConfigs.length;
        
        if (this.totalLogos === 0) {
          this.showFallback();
          return;
        }
        
        // Show loading indicator
        if (loadingIndicator) {
          loadingIndicator.style.display = 'block';
        }
        
        // Create and load logos
        for (const config of logoConfigs) {
          this.logoPromises.push(this.createAndLoadLogo(config, logoContainer));
        }
        
        // Wait for all logo attempts to complete
        try {
          await Promise.allSettled(this.logoPromises);
        } catch (error) {
          console.warn('Error loading logos:', error);
        }
        
        // Hide loading indicator
        if (loadingIndicator) {
          loadingIndicator.style.display = 'none';
        }
        
        // Show fallback if no logos loaded
        if (this.loadedLogos === 0) {
          this.showFallback();
        }
      }
      
      async createAndLoadLogo(config, container) {
        const img = document.createElement('img');
        img.alt = config.alt;
        img.className = config.className;
        container.appendChild(img);
        
        // Try main source first, then fallbacks
        const sources = [config.src, ...config.fallbackSrcs];
        
        for (const src of sources) {
          try {
            await this.loadImage(img, src);
            img.classList.add('loaded');
            this.loadedLogos++;
            return;
          } catch (error) {
            console.warn(\`Failed to load \${config.alt} from \${src}:\`, error);
          }
        }
        
        // If all sources failed, hide the image
        img.style.display = 'none';
        console.error(\`All sources failed for \${config.alt}\`);
      }
      
      loadImage(img, src) {
        return new Promise((resolve, reject) => {
          const tempImg = new Image();
          tempImg.onload = () => {
            img.src = src;
            resolve();
          };
          tempImg.onerror = reject;
          tempImg.src = src;
        });
      }
      
      showFallback() {
        const fallback = document.querySelector('.logo-fallback');
        if (fallback) {
          fallback.style.display = 'block';
        }
      }
    }
    
    // Auto-print functionality
    window.addEventListener('load', function() {
      // Initialize logo manager
      new LogoManager();
      
      // Focus the print button for better UX
      setTimeout(function() {
        const printButton = document.querySelector('.print-button');
        if (printButton) {
          printButton.focus();
        }
      }, 100);
      
      // Auto-print if requested
      setTimeout(function() {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('autoprint') === 'true') {
          window.print();
        }
      }, 1500); // Increased delay to allow logos to load
    });
  </script>
</head>
<body>
  <div class="no-print">
    <button class="print-button" onclick="window.print()">Print / Save as PDF</button>
  </div>
  
  <div class="header">
    <div class="logo-container">
      <div class="loading-indicator">Loading logos...</div>
      <div class="logo-fallback">${fallbackLogoText}</div>
    </div>
    
    <h1>${reportTitle}</h1>
    <p>Generated on: ${generatedDate}</p>
  </div>
  
  <table${reportType === "finehistory" ? ' class="finehistory-table"' : ""}>
    <thead>
      <tr>${tableHeaders}</tr>
    </thead>
    <tbody>
      ${tableRows}
    </tbody>
  </table>
  
  <div class="footer">
    <p>${footerText}</p>
    <p>Generated by Library Information Management System</p>
  </div>
</body>
</html>
`;
